import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import Loader2 from '../../components/loader2';
import showToast from '../../components/ShowToast';
import OnboardingProgressBar from '../../components/Projects/OnboardingProgressBar';
import './SelectProjectTemplate.css';
import ModernNavbarPureCSS from '../../components/Navbar/ModernNavBarPureCSS';
import { jwtDecode } from 'jwt-decode';

// 1. Import the specific icons you need from lucide-react
import { 
    ChefHat,         // for kitchen_icon
    Bath,            // for bathroom_icon
    Armchair,        // for deck_icon (implies outdoor living)
    Home,            // for home_icon
    Umbrella,        // for umbrella_icon
    PaintRoller,     // for paint_roller_icon
    Hammer,          // for tool_icon
    Fence,           // for fence_icon (Ensure lucide-react is updated, or use 'Shield')
    CarFront,        // for car_icon
    Waves,           // for waves_icon
    Key,             // for key_icon
    TrendingUp,      // for trending_up_icon
    FileText,        // for file_text_icon
    Construction,    // Fallback icon
    Wrench           // Alternative for tools
} from 'lucide-react';

// 2. Create a mapping object: DB String -> Icon Component
const iconMapping = {
    "kitchen_icon": ChefHat,
    "bathroom_icon": Bath,
    "deck_icon": Armchair,
    "home_icon": Home,
    "umbrella_icon": Umbrella,
    "paint_roller_icon": PaintRoller,
    "tool_icon": Hammer, 
    "fence_icon": Fence, 
    "car_icon": CarFront,
    "waves_icon": Waves,
    "key_icon": Key,
    "trending_up_icon": TrendingUp,
    "file_text_icon": FileText
};

const SelectProjectTemplate = () => {
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [username, setUsername] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUsername(decoded.UserName || decoded.unique_name || 'User');
            } catch (e) {
                console.error("Token decode error", e);
            }
        }
    }, []);

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/ProjectTemplates`);
                setTemplates(response.data);
            } catch (err) {
                setError('Failed to fetch project templates.');
                console.error('Error fetching project templates:', err);
                showToast('error', 'Failed to load project templates.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchTemplates();
    }, []);

    const handleTemplateSelect = (templateId) => {
        navigate(`/ai-project-onboarding/location/${templateId}`);
    };

    // 3. Helper function to render the icon safely
    const renderIcon = (iconName) => {
        // Find the icon in the map, or use 'Construction' as a default fallback
        const IconComponent = iconMapping[iconName] || Construction;
        
        // Render it with a class for styling
        return <IconComponent size={48} className="template-icon-svg" />;
    };

    if (isLoading) {
        return <Loader2 />;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div style={{minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', paddingTop: '60px'}}>
            <ModernNavbarPureCSS UserName={username} />
            
            {/* Gold Subtitle Bar */}
            <div style={{
                background: 'linear-gradient(to bottom, #d4af37 0%, #f9e68c 30%, #c49c25 60%, #8a6e14 100%)',
                padding: '1.5rem 2rem',
                borderTop: '1px solid #fffae0',
                borderBottom: '1px solid #49310a',
                color: 'white',
                marginBottom: '2rem'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#011746' }}>Select a Project Type</h1>
                        <p style={{ margin: '0.25rem 0 0 0', color: '#011746', opacity: 0.9 }}>Choose a pre-defined project template to get started with AI assistance.</p>
                    </div>
                    <button 
                        onClick={() => navigate('/projects')}
                        style={{
                            background: 'rgba(255, 255, 255, 0.2)',
                            border: '1px solid rgba(255, 255, 255, 0.4)',
                            color: '#011746',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>

        <div className="select-template-container">
            <OnboardingProgressBar currentStep={1} />

            <div className="template-grid">
                {templates.map(template => (
                    <div key={template.templateID} className="template-card" onClick={() => handleTemplateSelect(template.templateID)}>
                        
                        {/* 4. Replaced the <img> tag with our helper function */}
                        <div className="icon-wrapper">
                            {renderIcon(template.iconName)}
                        </div>

                        <h3>{template.name}</h3>
                        <p>{template.description}</p>
                    </div>
                ))}
            </div>

            {templates.length === 0 && !isLoading && (
                <p className="no-templates-message">No project templates available. Please contact support.</p>
            )}
        </div>
        </div>
    );
};

export default SelectProjectTemplate;
